import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ClipboardList,
  Settings,
  LogOut,
  X,
  ShoppingCart,
  Factory,
  Store,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  BookOpenCheck,
  Sparkles,
  // ShieldCheck
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import logo from '../../assets/image/system/logo.png';
import oneLogo from '../../assets/image/system/one-logo.png';

type NavChildItem = {
  name: string;
  href: string;
  icon?: React.ElementType;
};

type NavLeafItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  children?: never;
};

type NavGroupItem = {
  name: string;
  icon: React.ElementType;
  children: NavChildItem[];
  href?: never;
};

type NavItem = NavLeafItem | NavGroupItem;
type ExpandedGroupState = NavGroupItem['name'] | null;

 const getNavigation = (
  canReadAssistant: boolean,
  canReadReports: boolean,
  canReadMembers: boolean,
  canReadPO: boolean,
  canReadSuppliers: boolean,
  canReadLots: boolean,
  canReadSamples: boolean,
  canReadInventory: boolean,
  canReadWO: boolean,
  canReadRecipes: boolean,
  canReadArtisan: boolean,
  canReadCupping: boolean,
  canReadSO: boolean,
  canReadCustomers: boolean,
  canReadMachines: boolean,
  canReadRoasters: boolean,
  canReadSubscription: boolean,
  // isSuperAdmin: boolean
): NavItem[] => [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  ...(canReadAssistant ? [{ name: 'Qora Assistant', href: '/qora-assistant', icon: Sparkles }] : []),
  ...((canReadPO || canReadSuppliers || canReadLots || canReadSamples) ? [{
    name: 'Purchasing',
    icon: ShoppingCart,
    children: [
      ...(canReadPO ? [{ name: 'Purchase Order', href: '/purchase-orders' }] : []),
      ...(canReadSuppliers ? [{ name: 'Supplier Master', href: '/purchasing/suppliers' }] : []),
      ...(canReadLots ? [{ name: 'Lot List', href: '/purchasing/lots' }] : []),
      ...(canReadSamples ? [{ name: 'Sample List', href: '/purchasing/samples' }] : []),
    ]
  }] : []),
  ...(canReadInventory ? [{ name: 'Inventory', href: '/inventory', icon: Package }] : []),
  ...((canReadWO || canReadRecipes || canReadArtisan) ? [{
    name: 'Production',
    icon: Factory,
    children: [
      ...(canReadWO ? [{ name: 'Work Order', href: '/work-orders' }] : []),
      ...(canReadRecipes ? [{ name: 'Master Profile', href: '/recipes' }] : []),
      ...(canReadArtisan ? [{ name: 'Artisan Sync', href: '/artisan-sync' }] : []),
    ]
  }] : []),
  ...(canReadCupping ? [{
    name: 'Quality Control',
    icon: BookOpenCheck,
    children: [
      { name: 'Cupping Session', href: '/cupping-sessions'}
    ]
  }] : []),
  ...((canReadSO || canReadCustomers) ? [{
    name: 'Selling',
    icon: Store,
    children: [
      ...(canReadSO ? [{ name: 'Sales Order', href: '/sales-orders' }] : []),
      ...(canReadCustomers ? [{ name: 'Customer Master', href: '/selling/customers' }] : [])
    ]
  }] : []),
  ...(canReadReports ? [{
    name: 'Report',
    icon: ClipboardList,
    children: [
      { name: 'General Report', href: '/reports/general' },
    ]
  }] : []),
  {
    name: 'Settings',
    icon: Settings,
    children: [
      { name: 'General', href: '/settings' },
      ...(canReadMembers ? [{ name: 'Member', href: '/settings/member' }] : []),
      ...(canReadMembers ? [{ name: 'Roles & Permissions', href: '/settings/roles' }] : []),
      ...(canReadMachines ? [{ name: 'Roasting Machine', href: '/production/machines' }] : []),
      ...(canReadRoasters ? [{ name: 'Roast Master', href: '/production/roasters' }] : []),
      ...(canReadSubscription ? [{ name: 'Subscription', href: '/settings/subscription' }] : []),
    ]
  },
  // ...(isSuperAdmin
  //   ? [{ name: 'Admin Console', href: '/admin', icon: ShieldCheck } as NavLeafItem]
  //   : []),
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed = false, onToggleCollapse }) => {
  const logout = useAuthStore((state) => state.logout);
  const { t } = useLanguage();
  const { can } = usePermissions();
  const location = useLocation();
  const webVersion = import.meta.env.VITE_WEB_VERSION || 'v0.0.0';
  const isStaging = import.meta.env.VITE_ENV === 'staging';
  const [expandedGroup, setExpandedGroup] = useState<ExpandedGroupState>(null);
  // const userRole = useAuthStore((state) => state.user?.role);
  // const isSuperAdmin =
  //   (userRole ?? '').trim().toLowerCase().replace(/\s+/g, '_') === 'super_admin';
  const canReadMembers = can('members', 'read');
  const canReadReports =
    can('reports.roasting', 'read') ||
    can('reports.green_beans', 'read') ||
    can('reports.roasted_beans', 'read') ||
    can('reports.business', 'read');
  const canReadAssistant = can('qora_assistant', 'write');
  const canReadPO = can('purchase_orders', 'read');
  const canReadSuppliers = can('suppliers', 'read');
  const canReadLots = can('inventory', 'read'); // Lots are part of inventory/lots
  const canReadSamples = can('samples', 'read');
  const canReadInventory = can('inventory', 'read');
  const canReadWO = can('work_orders.roasting', 'read') || can('work_orders.packing', 'read');
  const canReadRecipes = can('recipes', 'read');
  const canReadArtisan = can('artisan_profiles', 'read');
  const canReadCupping = can('cupping_sessions', 'read');
  const canReadSO = can('sales_orders', 'read');
  const canReadCustomers = can('customers', 'read');
  const canReadMachines = can('machines', 'read');
  const canReadRoasters = can('roasters', 'read');
  const canReadSubscription = can('subscription', 'read');

  const navigation = getNavigation(
    canReadAssistant,
    canReadReports,
    canReadMembers,
    canReadPO,
    canReadSuppliers,
    canReadLots,
    canReadSamples,
    canReadInventory,
    canReadWO,
    canReadRecipes,
    canReadArtisan,
    canReadCupping,
    canReadSO,
    canReadCustomers,
    canReadMachines,
    canReadRoasters,
    canReadSubscription,
    // isSuperAdmin
  );

  const translateNavigationLabel = (name: string): string => {
    const keyMap: Record<string, string> = {
      Dashboard: 'sidebar.dashboard',
      Purchasing: 'sidebar.purchasing',
      'Purchase Order': 'sidebar.purchaseOrder',
      'Supplier Master': 'sidebar.supplierMaster',
      'Lot List': 'sidebar.lotList',
      'Sample List': 'sidebar.sampleList',
      Inventory: 'sidebar.inventory',
      Production: 'sidebar.production',
      'Work Order': 'sidebar.workOrder',
      'Master Profile': 'sidebar.masterProfile',
      'Artisan Sync': 'sidebar.artisanSync',
      'Quality Control': 'sidebar.qualityControl',
      'Cupping Session': 'sidebar.cuppingSession',
      Selling: 'sidebar.selling',
      'Sales Order': 'sidebar.salesOrder',
      'Customer Master': 'sidebar.customerMaster',
      Report: 'sidebar.report',
      'General Report': 'sidebar.generalReport',
      Settings: 'sidebar.settings',
      General: 'sidebar.general',
      Member: 'sidebar.member',
      'Roasting Machine': 'sidebar.roastingMachine',
      'Roast Master': 'sidebar.roastMaster',
      'Qora Assistant': 'sidebar.qoraAssistant',
      Subscription: 'sidebar.subscription',
      'Admin Console': 'sidebar.adminConsole',
    };
    return t(keyMap[name] ?? name, undefined, name);
  };

  const toggleGroup = (name: NavGroupItem['name']) => {
    if (isCollapsed && onToggleCollapse) {
      onToggleCollapse();
      setExpandedGroup(name);
      return;
    }

    setExpandedGroup((prev) => (prev === name ? null : name));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const isActiveLink = (href: string) => {
    if (href === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(href);
  };

  const isGroupActive = (item: NavItem) => {
    if (!item.children) return false;
    return item.children.some(child => isActiveLink(child.href));
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity md:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        data-test-id="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-[#24292E] text-white transition-all duration-300 transform md:translate-x-0 md:fixed md:flex md:flex-col h-full",
          isOpen ? "translate-x-0" : "-translate-x-full",
          isCollapsed ? "w-20" : "w-64"
        )}>
        <div className={cn("relative flex items-center h-16 border-b border-gray-800 justify-center", isCollapsed ? "px-0" : "px-4")}>
          {!isCollapsed && (
            <img src={logo} alt={t('sidebar.logoAlt')} className="h-12 w-auto px-1" />
          )}
          {isCollapsed && <img src={oneLogo} alt={t('sidebar.logoAlt')} className="h-9 w-auto px-1" />}
          <button
            onClick={onClose}
            data-test-id="sidebar-close-mobile"
            className="absolute right-4 md:hidden text-gray-400 hover:text-white focus:outline-none"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className={cn(
          "flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden",
          "scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent",
          "scrollbar-gutter-stable",
          "[&::-webkit-scrollbar]:w-2",
          "[&::-webkit-scrollbar-track]:bg-transparent",
          "[&::-webkit-scrollbar-thumb]:bg-gray-500",
          "[&::-webkit-scrollbar-thumb]:rounded-full",
          "hover:[&::-webkit-scrollbar-thumb]:bg-gray-600"
        )}>
          {navigation.map((item) => {
            if (item.children) {
              const isExpanded = expandedGroup === item.name && !isCollapsed;
              const active = isGroupActive(item);

              return (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => toggleGroup(item.name)}
                    data-test-id={`sidebar-group-toggle-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    title={isCollapsed ? translateNavigationLabel(item.name) : undefined}
                    className={cn(
                      'flex items-center w-full py-2 text-sm font-medium rounded-md transition-colors',
                      isCollapsed ? 'justify-center px-2' : 'justify-between px-4',
                      active ? 'text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    )}
                  >
                    <div className="flex items-center">
                      <item.icon className={cn("h-5 w-5", !isCollapsed && "mr-3")} aria-hidden="true" />
                      {!isCollapsed && translateNavigationLabel(item.name)}
                    </div>
                    {!isCollapsed && (
                      isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )
                    )}
                  </button>

                  {!isCollapsed && (
                    <div
                      className={cn(
                        "grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-in-out",
                        isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0 pointer-events-none"
                      )}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="pl-11 space-y-1 pt-1">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.name}
                              to={child.href}
                              onClick={() => onClose()}
                              data-test-id={`sidebar-nav-child-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                              end={child.href === '/settings'}
                              className={({ isActive }) =>
                                cn(
                                  'flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors',
                                  isActive
                                    ? 'bg-gray-800 text-white'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                )
                              }
                            >
                              {child.icon && <child.icon className="mr-3 h-4 w-4" aria-hidden="true" />}
                              {translateNavigationLabel(child.name)}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <NavLink
                key={item.name}
                to={item.href!}
                onClick={() => onClose()}
                data-test-id={`sidebar-nav-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                title={isCollapsed ? translateNavigationLabel(item.name) : undefined}
                end={item.href === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center py-2 text-sm font-medium rounded-md transition-colors',
                    isCollapsed ? 'justify-center px-2' : 'px-4',
                    isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  )
                }
              >
                <item.icon className={cn("h-5 w-5", !isCollapsed && "mr-3")} aria-hidden="true" />
                {!isCollapsed && translateNavigationLabel(item.name)}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-800 space-y-2">
          {/* Desktop Collapse Toggle */}
          <button
            onClick={onToggleCollapse}
            data-test-id="sidebar-collapse-toggle"
            className="hidden md:flex items-center w-full px-4 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors justify-center"
            title={
              isCollapsed
                ? t('common.actions.expandSidebar')
                : t('common.actions.collapseSidebar')
            }
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <div className="flex items-center w-full">
                <ChevronLeft className="mr-3 h-5 w-5" />
                <span>{t('common.actions.collapse')}</span>
              </div>
            )}
          </button>

          <button
            onClick={() => logout()}
            data-test-id="sidebar-logout-button"
            className={cn(
              "flex items-center w-full py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-800 hover:text-white transition-colors",
              isCollapsed ? "justify-center px-2" : "px-4"
            )}
            title={isCollapsed ? t('common.actions.logout') : undefined}
          >
            <LogOut className={cn("h-5 w-5", !isCollapsed && "mr-3")} aria-hidden="true" />
            {!isCollapsed && t('common.actions.logout')}
          </button>
          {isStaging && (
            <div
              className={cn(
                "flex items-center",
                isCollapsed ? "justify-center px-1" : "px-4"
              )}
              title="Staging environment"
              data-test-id="sidebar-staging-indicator"
            >
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full bg-amber-500/90 font-semibold tracking-wide text-white ring-1 ring-amber-600/60",
                  isCollapsed ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                {isCollapsed ? 'STG' : 'STAGING'}
              </span>
            </div>
          )}
          <div
            className={cn(
              "text-[11px] text-gray-500",
              isCollapsed ? "text-center px-1" : "px-4 text-left"
            )}
            title={webVersion}
          >
            {webVersion}
          </div>
        </div>
      </div>
    </>
  );
};
